/**
 * HiddenStagesPanel — FTP's "Hidden Stages" panel beside the automation
 * tables: the stages hidden for the selected stage on the selected pipeline.
 * Plans see them only in the progress bar's "…" menu, so a hidden pill's
 * settings offer "move to" alone. The panel takes a dropped pill (hide), and a
 * hidden pill dropped back on the pipeline row shows it again. On a Locked
 * page (plan 2026-10-10-001, U7) an empty panel drops its "drag here" hint.
 */
import { useDroppable } from '@dnd-kit/core';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { cn } from '@/lib/utils';
import { PILL_DROP } from './pillDnd';
import type { PillCallbacks } from './PipelineRow';
import { StagePill } from './StagePill';
import type { PillModel } from './stagePipelineModel';

export interface HiddenStagesPanelProps extends PillCallbacks {
  pills: PillModel[];
  className?: string;
}

export function HiddenStagesPanel({ pills, openPillId, onOpenPillChange, onRuleChange, className }: HiddenStagesPanelProps) {
  const { setNodeRef, isOver } = useDroppable({ id: PILL_DROP.hidden });
  const empty = pills.length === 0;
  const locked = useAdminPageLevel() === 'locked';
  return (
    <section
      ref={setNodeRef}
      data-testid="hidden-stages"
      aria-label="Hidden Stages"
      className={cn(
        'w-56 shrink-0 rounded-lg border bg-slate-50 p-3 transition-colors',
        // Dashed only while empty: it is then just a drop zone.
        empty ? 'border-dashed border-slate-300' : 'border-slate-200',
        isOver && 'border-solid border-[#3a7fd5] bg-blue-50',
        className,
      )}
    >
      <h3 className="mb-2 text-center text-xs font-semibold text-slate-500">Hidden Stages</h3>
      {empty ? (
        !locked && <p className="py-4 text-center text-xs text-slate-400">Drag a pill onto … to hide it here.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {pills.map((p) => (
            <StagePill
              key={p.stage.id}
              pill={p}
              hidden
              className="w-full"
              open={openPillId === p.stage.id}
              onOpenChange={(open) => onOpenPillChange(p.stage.id, open)}
              onChange={(patch) => onRuleChange(p.stage.id, patch)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
